/**
 * Permissions Registry
 * 
 * This is the single source of truth for all available permissions in the application.
 * Routes are only accessible if they are defined in this registry.
 * 
 * When adding a new page:
 * 1. Add it to the pages array below
 * 2. The route will automatically be secured based on this registry
 * 3. The permission builder will automatically include it (unless showInBuilder: false)
 * 
 * Properties:
 * - showInBuilder: (optional, default: true) Whether this entity should appear in the permission builder UI
 */

// All available pages - must match Routes.js permissionedPages
export const PAGES = [
    { key: 'documents', displayName: 'Documents', module: 'DOCUMENTS', path: '/documents', showInBuilder: false },
  { key: 'absence-management', displayName: 'Absence Management', module: 'HR', path: '/absence-management', showInBuilder: true },
  { key: 'actrans', displayName: 'ACTRANS by Period', module: 'ACTIVITY', path: '/actrans', showInBuilder: true },
  { key: 'actrans-activity', displayName: 'ACTRANS by Activity', module: 'ACTIVITY', path: '/actrans-activity', showInBuilder: true },
  { key: 'employee', displayName: 'Employee', module: 'HR', path: '/employee', showInBuilder: true },
  { key: 'employee-advanced-search', displayName: 'Employee Advanced Search', module: 'HR', path: '/employee-advanced-search', showInBuilder: true },
  { key: 'assets', displayName: 'Asset (AM20)', module: 'ASSET', path: '/assets', showInBuilder: true },
  { key: 'asset-analysis', displayName: 'Asset Analysis (AM70.1)', module: 'ASSET', path: '/asset-analysis', showInBuilder: true },
  { key: 'transaction-analysis', displayName: 'Transaction Analysis (AM80.1)', module: 'ASSET', path: '/transaction-analysis', showInBuilder: true },
  { key: 'cash-book-trans', displayName: 'Cash Book Trans (CB80)', module: 'CASH', path: '/cash-book-trans', showInBuilder: true },
  { key: 'cash-payment-inquiry', displayName: 'Cash Payment (CB 56)', module: 'CASH', path: '/cash-payment-inquiry', showInBuilder: true },
  { key: 'gl-accounts', displayName: 'Chart Of Accounts', module: 'GL', path: '/gl-accounts', showInBuilder: true },
  { key: 'gl-trans', displayName: 'Transactions', module: 'GL', path: '/gl-trans', showInBuilder: true },
//   { key: 'gl-trans-v2', displayName: 'Transactions V2', module: 'GL', path: '/gl-trans-v2', showInBuilder: true },
//   { key: 'gl-trans-no-select', displayName: 'Transactions No Select', module: 'GL', path: '/gl-trans-no-select', showInBuilder: true },
  { key: 'item', displayName: 'Item (IC11)', module: 'PO', path: '/item', showInBuilder: true },
  { key: 'item-loc', displayName: 'Item Location / Transactions (IC12/IC50)', module: 'PO', path: '/item-loc', showInBuilder: true },
  { key: 'vendor', displayName: 'Accounts Payable', module: 'AP', path: '/vendor', showInBuilder: true },
  { key: 'invoice-lookup', displayName: 'Invoice Lookup', module: 'AP', path: '/invoice-lookup', showInBuilder: true },
  { key: 'invoice-matching', displayName: 'Invoice Matching', module: 'AP', path: '/invoice-matching', showInBuilder: true },
  { key: 'po', displayName: 'PO', module: 'PO', path: '/po', showInBuilder: true },
  { key: 'requisition-inquiry', displayName: 'Requisition (RQ44)', module: 'PO', path: '/requisition-inquiry', showInBuilder: true },
  { key: 'trial-balances', displayName: 'Trial Balances', module: 'GL', path: '/trial-balances', showInBuilder: true },
  { key: 'trial-balances-accounts', displayName: 'Trial Balances by Account', module: 'GL', path: '/trial-balances-accounts', showInBuilder: true },
  { key: 'trial-balances-consolidated', displayName: 'Trial Balances Consolidated', module: 'GL', path: '/trial-balances-consolidated', showInBuilder: true },
  { key: 'customer-activity', displayName: 'Accounts Receivable', module: 'AR', path: '/customer-activity', showInBuilder: true },
  { key: 'bill-cost', displayName: 'Bill of Cost (AC471)', module: 'ACTIVITY', path: '/bill-cost', showInBuilder: true },
  { key: 'metadata', displayName: 'Metadata', module: null, path: '/metadata', showInBuilder: true },
  { key: 'journal-control', displayName: 'Journal Control (GL45)', module: 'GL', path: '/journal-control', showInBuilder: true },
  { key: 'po-inquiry', displayName: 'PO Search', module: 'PO', path: '/po-inquiry', showInBuilder: true },
  { key: 'po-receipt', displayName: 'PO Receipt', module: 'PO', path: '/po-receipt', showInBuilder: true },
  { key: 'vendor-agreement', displayName: 'Vendor Agreement', module: 'PO', path: '/vendor-agreement', showInBuilder: true },
  { key: 'athena-report-builder', displayName: 'Athena Report Builder', module: null, path: '/athena-report-builder', showInBuilder: false },
  { key: 'account', displayName: 'User Account', module: null, path: '/account', showInBuilder: false },
  { key: 'lp51', displayName: 'LOA (LP51)', module: 'HR', path: '/lp51', showInBuilder: true },
  { key: 'length-of-service', displayName: 'Length of Service', module: 'HR', path: '/length-of-service', showInBuilder: true },
  { key: 'garnishment', displayName: 'Garnishments (PR26)', module: 'HR', path: '/garnishment', showInBuilder: true },
  { key: 'payroll-distribution', displayName: 'Payroll Distribution', module: 'HR', path: '/payroll-distribution', showInBuilder: true },
  { key: 'resource', displayName: 'Resource', module: 'HR', path: '/resource', showInBuilder: true },
  // Special pages that require explicit permission check
  { key: 'admin', displayName: 'Admin', module: null, path: '/admin', showInBuilder: true },
  { key: 'report-builder', displayName: 'Report Builder', module: null, path: '/report-builder', showInBuilder: false },
  { key: 'reports', displayName: 'Reports', module: null, path: '/reports', showInBuilder: true },
];

// All available report categories
// These should match what's available in your reports catalog
export const REPORT_CATEGORIES = [
  { key: 'HR', displayName: 'Human Resources', showInBuilder: true },
  { key: 'PR', displayName: 'Payroll', showInBuilder: true },
  { key: 'AP', displayName: 'Accounts Payable', showInBuilder: true },
  { key: 'AR', displayName: 'Accounts Receivable', showInBuilder: true },
  { key: 'GL', displayName: 'General Ledger', showInBuilder: false },
  { key: 'GM', displayName: 'Grant Management', showInBuilder: true },
  { key: 'PO', displayName: 'Procurement', showInBuilder: true },
  { key: 'ASSET', displayName: 'Asset Management', showInBuilder: false },
  { key: 'CASH', displayName: 'Cash Book', showInBuilder: false },
  { key: 'ACTIVITY', displayName: 'Activity', showInBuilder: true },
  { key: 'VENDOR', displayName: 'Vendor', showInBuilder: false },
  { key: 'AUDIT', displayName: 'Audit', showInBuilder: false },
];

// Subpages structure (nested permissions within pages)
// Format: { parentModule: [{ key, displayName, tabValue, showInBuilder }] }
// tabValue is the index used in the tab component
export const SUBPAGES = {
  HR: [
    { key: 'main', displayName: 'Main', tabValue: 0, showInBuilder: true },
    { key: 'pay-rate-history', displayName: 'Pay Rate History', tabValue: 1, showInBuilder: true },
    { key: 'personnel-action', displayName: 'Personnel Action', tabValue: 2, showInBuilder: true },
    { key: 'tax', displayName: 'Tax PR13', tabValue: 3, showInBuilder: true },
    { key: 'deductions', displayName: 'Deductions (PR15.2)', tabValue: 4, showInBuilder: true },
    { key: 'payroll-history', displayName: 'Payroll History (PR52.1)', tabValue: 5, showInBuilder: true },
    { key: 'pay-stubs', displayName: 'Pay Stubs', tabValue: 6, showInBuilder: true },
    { key: 'field-history', displayName: 'Field History', tabValue: 7, showInBuilder: true },
    { key: 'user-fields', displayName: 'User Fields', tabValue: 8, showInBuilder: true },
    { key: 'benefits', displayName: 'Benefits', tabValue: 9, showInBuilder: true },
    { key: 'beneficiaries', displayName: 'Beneficiaries', tabValue: 10, showInBuilder: true },
    { key: 'dependents', displayName: 'Dependents', tabValue: 11, showInBuilder: true },
    { key: 'position-history', displayName: 'Position History', tabValue: 12, showInBuilder: true },
    // Personnel Action subpages (sub-subpages - not rendered as top-level tabs)
    { key: 'personnel-action-history', displayName: 'Personnel Action History', tabValue: 14, showInBuilder: true, parent: 'personnel-action' },
    { key: 'personnel-action-reviews', displayName: 'Personnel Action Reviews', tabValue: 15, showInBuilder: true, parent: 'personnel-action' },
    { key: 'personnel-action-certifications', displayName: 'Personnel Action Certifications', tabValue: 16, showInBuilder: true, parent: 'personnel-action' },
    { key: 'personnel-action-disciplinary', displayName: 'Personnel Action Disciplinary', tabValue: 17, showInBuilder: true, parent: 'personnel-action' },
    { key: 'personnel-action-comments', displayName: 'Personnel Action Comments', tabValue: 18, showInBuilder: true, parent: 'personnel-action' },
    { key: 'personnel-action-competencies', displayName: 'Personnel Action Competencies', tabValue: 19, showInBuilder: true, parent: 'personnel-action' },
  ],
  AP: [
    { key: 'main', displayName: 'Main', tabValue: 0, showInBuilder: true },
    { key: 'invoices', displayName: 'Invoices', tabValue: 1, showInBuilder: true },
    { key: 'locations', displayName: 'Locations', tabValue: 2, showInBuilder: true },
    { key: 'po', displayName: 'PO', tabValue: 3, showInBuilder: true },
    { key: 'bank', displayName: 'Bank', tabValue: 4, showInBuilder: true },
    { key: 'agreements', displayName: 'Vendor Agreements', tabValue: 5, showInBuilder: true },
  ],
  PO: [
    { key: 'agreements', displayName: 'Vendor Agreements', tabValue: 0, showInBuilder: true },
  ],
  // Add other module subpages here as needed
  // GL: [...],
};

// Helper function to get page by key
export const getPageByKey = (key) => {
  return PAGES.find(page => page.key === key);
};

// Helper function to get all pages for a module
export const getPagesByModule = (module) => {
  return PAGES.filter(page => page.module === module);
};

// Helper function to check if a page key exists in registry
export const isPageInRegistry = (key) => {
  return PAGES.some(page => page.key === key);
};

// Helper function to get all page keys
export const getAllPageKeys = () => {
  return PAGES.map(page => page.key);
};

// Helper function to get all report category keys
export const getAllReportCategoryKeys = () => {
  return REPORT_CATEGORIES.map(cat => cat.key).sort((a, b) => a.localeCompare(b));
};

// Helper function to get subpages for a module
export const getSubpagesByModule = (module) => {
  return SUBPAGES[module] || [];
};

// Helper function to get subpage by module and key
export const getSubpageByKey = (module, key) => {
  const moduleSubpages = SUBPAGES[module] || [];
  return moduleSubpages.find(subpage => subpage.key === key);
};

// Helper function to check if a subpage exists in registry
export const isSubpageInRegistry = (module, key) => {
  return getSubpageByKey(module, key) !== undefined;
};

// Helper function to get pages that should show in builder
export const getPagesForBuilder = () => {
  return PAGES.filter(page => page.showInBuilder !== false);
};

// Helper function to get report categories that should show in builder
export const getReportCategoriesForBuilder = () => {
  return REPORT_CATEGORIES.filter(cat => cat.showInBuilder !== false);
};

// Helper function to get subpages for a module that should show in builder
export const getSubpagesForBuilder = (module) => {
  const moduleSubpages = SUBPAGES[module] || [];
  return moduleSubpages.filter(subpage => subpage.showInBuilder !== false);
};

// Helper function to get sub-subpages (children) for a given parent subpage
export const getSubSubpagesByParent = (module, parentKey) => {
  const moduleSubpages = SUBPAGES[module] || [];
  return moduleSubpages.filter(subpage => subpage.parent === parentKey);
};

// Helper function to get all unique modules from pages
export const getAllModulesFromPages = () => {
  const modules = new Set();
  PAGES.forEach(page => {
    if (page.module) {
      modules.add(page.module);
    }
  });
  return Array.from(modules).sort();
};

// Helper function to get all unique modules from report categories
export const getAllModulesFromReportCategories = () => {
  const modules = new Set();
  REPORT_CATEGORIES.forEach(cat => {
    // Report categories are keyed by module name
    if (cat.key) {
      modules.add(cat.key);
    }
  });
  return Array.from(modules).sort();
};

// Helper function to get all unique modules from subpages
export const getAllModulesFromSubpages = () => {
  return Object.keys(SUBPAGES).sort();
};

// Helper function to get all unique modules across all permission types
// This is the comprehensive list of all modules in the system
export const getAllModules = () => {
  const modules = new Set();
  
  // Get from pages
  PAGES.forEach(page => {
    if (page.module) {
      modules.add(page.module);
    }
  });
  
  // Get from report categories (they're keyed by module)
  REPORT_CATEGORIES.forEach(cat => {
    if (cat.key) {
      modules.add(cat.key);
    }
  });
  
  // Get from subpages
  Object.keys(SUBPAGES).forEach(module => {
    modules.add(module);
  });
  
  return Array.from(modules).sort();
};

// Helper function to resolve enabled modules (handles wildcard "*")
export const resolveEnabledModules = (clientModules) => {
  // If it's already an array, return it
  if (Array.isArray(clientModules)) {
    return clientModules;
  }
  
  // If it's a string
  if (typeof clientModules === 'string') {
    // If wildcard, return all modules
    if (clientModules.trim() === '*') {
      return getAllModules();
    }
    // Otherwise, parse comma-separated list
    return clientModules
      .split(',')
      .map(m => m.trim())
      .filter(m => m.length > 0);
  }
  
  // Default: return all modules (fail open)
  return getAllModules();
};

// Helper function to check if a module is enabled
export const isModuleEnabled = (module, enabledModules) => {
  if (!module) return true; // Pages without modules are always enabled
  const resolved = resolveEnabledModules(enabledModules);
  return resolved.includes(module);
};

// Complete permissions registry structure
export const PERMISSIONS_REGISTRY = {
  pages: PAGES,
  reportCategories: REPORT_CATEGORIES,
  subpages: SUBPAGES,
  // Helper functions
  getPageByKey,
  getPagesByModule,
  isPageInRegistry,
  getAllPageKeys,
  getAllReportCategoryKeys,
  getSubpagesByModule,
  getSubpageByKey,
  isSubpageInRegistry,
  getPagesForBuilder,
  getReportCategoriesForBuilder,
  getSubpagesForBuilder,
  getSubSubpagesByParent,
  getAllModulesFromPages,
  getAllModulesFromReportCategories,
  getAllModulesFromSubpages,
  getAllModules,
  resolveEnabledModules,
  isModuleEnabled,
};

export default PERMISSIONS_REGISTRY;

